import cardValidator from 'card-validator'
import { suffixes } from './suffixes'

export interface Validator {
  key: string
  errorMessage: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validation: (state: Record<string, any>) => boolean
}

export interface ValidationMessages {
  key: string
  errorMessage: string
}

export const validate = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: Record<string, any>,
  validators: Validator[]
): ValidationMessages[] =>
  validators.reduce((acc: ValidationMessages[], v) => {
    if (!v.validation(state)) {
      acc.push({ key: v.key, errorMessage: v.errorMessage })
    }
    return acc
  }, [])

export const requiredStringValidation = (value?: string | null) =>
  Boolean(value && value.length)

export const requiredNumericValidation = (value?: number | null) =>
  Boolean(value && value >= 0)

export const minLengthStringValidation = (
  value: string | null,
  length: number
) => Boolean(value && value.length >= length)

export const maxLengthStringValidation = (
  value: string | null,
  length: number
) => Boolean(value && value.length <= length)

export const patternValidation = (value: string | null, pattern: string) => {
  const regex = new RegExp(pattern, 'g')
  return Boolean(value && regex.test(value))
}

export const minArrayLengthValidation = (
  value: Array<Record<string, unknown>> | null,
  length: number
) => Boolean(value && value.length >= length)

export const maxArrayLengthValidation = (
  value: Array<Record<string, unknown>> | null,
  length: number
) => Boolean(value && value.length <= length)

const whiteList = ['visa', 'mastercard', 'american-express', 'discover']

const acceptedCard = (type: string) => whiteList.includes(type)

export const cardNumberValidation = (value: string) => {
  const validator = cardValidator.number(value)
  const { card } = validator
  const result = (validator.isValid && card && acceptedCard(card.type)) || false
  return result
}

const CC_EXPIRATION_MAX_ELAPSED_YEAR = 50

export const expDateValidation = (value: string) => {
  if (value.length < 7) return false
  return cardValidator.expirationDate(value, CC_EXPIRATION_MAX_ELAPSED_YEAR)
    .isValid
}

export const cvvValidation = (value: string) => {
  return cardValidator.cvv(value).isValid
}

export const zipCodeValidation = (value: string) => {
  return cardValidator.postalCode(value, { minLength: 5 }).isValid
}

export const lastNameValidation = (value: string) => {
  value = value.trim()
  if (!value) {
    return true
  }

  // should not contain any digit
  if (/\d/.test(value)) {
    return false
  }

  // value contains no space
  if (!/\s/g.test(value)) {
    return true
  }

  const array = value.split(' ')
  const lastWord = array[array.length - 1].toLocaleLowerCase()
  return suffixes.indexOf(lastWord) === -1
}

/** every prop is nullish (null or undefined) or a non-emptry string */
export const isNonNullishFilledStringObject = (x: unknown): boolean => {
  return (
    typeof x === 'object' &&
    x !== null &&
    Object.values(x).every((value) => value != null && value !== '')
  )
}

/** every prop is nullish (null or undefined) on the object */
export const isNullishObject = (x: unknown): boolean => {
  return (
    typeof x === 'object' &&
    x !== null &&
    Object.values(x).every((value) => value == null)
  )
}
